import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import { TrimmedNonEmptyString } from "./baseSchemas.js";
import { ProviderDriverKind } from "./providerInstance.js";
export const ProviderOptionDescriptorType = Schema.Literals(["select", "boolean"]);
export const ProviderOptionChoice = Schema.Struct({
    id: TrimmedNonEmptyString,
    label: TrimmedNonEmptyString,
    description: Schema.optional(TrimmedNonEmptyString),
    isDefault: Schema.optional(Schema.Boolean),
});
const ProviderOptionDescriptorBase = {
    id: TrimmedNonEmptyString,
    label: TrimmedNonEmptyString,
    description: Schema.optional(TrimmedNonEmptyString),
};
export const SelectProviderOptionDescriptor = Schema.Struct({
    ...ProviderOptionDescriptorBase,
    type: Schema.Literal("select"),
    options: Schema.Array(ProviderOptionChoice),
    currentValue: Schema.optional(TrimmedNonEmptyString),
    promptInjectedValues: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
});
export const BooleanProviderOptionDescriptor = Schema.Struct({
    ...ProviderOptionDescriptorBase,
    type: Schema.Literal("boolean"),
    currentValue: Schema.optional(Schema.Boolean),
});
export const ProviderOptionDescriptor = Schema.Union([
    SelectProviderOptionDescriptor,
    BooleanProviderOptionDescriptor,
]);
export const ProviderOptionSelectionValue = Schema.Union([TrimmedNonEmptyString, Schema.Boolean]);
export const ProviderOptionSelection = Schema.Struct({
    id: TrimmedNonEmptyString,
    value: ProviderOptionSelectionValue,
});
/**
 * Legacy on-disk shape for provider option selections, kept readable by the
 * decoder so we can tolerate stored data written before the v3 array shape.
 *
 * Persisted historically as `{ effort: "max", fastMode: true, ... }` inside
 * `modelSelection.options`. Migration 026 rewrites stored rows to the
 * canonical array shape, but we still see the legacy form in:
 *   - `settings.json` files from older client builds,
 *   - SQLite databases that have not yet run migration 026,
 *   - any future regression that re-introduces the legacy shape.
 */
const LegacyProviderOptionSelectionsObject = Schema.Record(Schema.String, Schema.Unknown);
const ProviderOptionSelectionsFromLegacyObject = LegacyProviderOptionSelectionsObject.pipe(Schema.decodeTo(Schema.Array(ProviderOptionSelection), SchemaTransformation.transformOrFail({
    decode: (record) => Effect.succeed(coerceLegacyOptionsObjectToArray(record)),
    encode: (selections) => Effect.succeed(canonicalSelectionsToLegacyObject(selections)),
})));
/**
 * Schema for the `options` field of every `ModelSelection` variant.
 *
 * Accepts both:
 *   - the canonical array shape `Array<{ id, value }>` (preferred), and
 *   - the legacy object shape `Record<string, string | boolean | …>` from
 *     pre-migration data.
 *
 * Always normalizes to the canonical array on decode and re-encodes as the
 * canonical array, so any legacy storage gets cleaned up the next time the
 * containing record is written back.
 */
export const ProviderOptionSelections = Schema.Union([
    Schema.Array(ProviderOptionSelection),
    ProviderOptionSelectionsFromLegacyObject,
]);
function coerceLegacyOptionsObjectToArray(record) {
    const entries = [];
    for (const [rawKey, rawValue] of Object.entries(record)) {
        const id = typeof rawKey === "string" ? rawKey.trim() : "";
        if (id.length === 0)
            continue;
        if (typeof rawValue === "string") {
            const trimmed = rawValue.trim();
            if (trimmed.length > 0)
                entries.push({ id, value: trimmed });
        }
        else if (typeof rawValue === "boolean") {
            entries.push({ id, value: rawValue });
        }
        // Drop anything else (numbers, null, nested objects/arrays) to match the
        // permissive normalization performed by migration 026.
    }
    return entries;
}
function canonicalSelectionsToLegacyObject(selections) {
    const out = {};
    for (const { id, value } of selections) {
        out[id] = value;
    }
    return out;
}
export const ModelCapabilities = Schema.Struct({
    optionDescriptors: Schema.optional(Schema.Array(ProviderOptionDescriptor)),
});
const CODEX_DRIVER_KIND = ProviderDriverKind.make("codex");
const CLAUDE_DRIVER_KIND = ProviderDriverKind.make("claudeAgent");
export const DEFAULT_MODEL = "gpt-5.6-sol";
export const DEFAULT_MODEL_BY_PROVIDER = {
    [CODEX_DRIVER_KIND]: DEFAULT_MODEL,
    [CLAUDE_DRIVER_KIND]: "claude-sonnet-4-6",
};
export const MODEL_SLUG_ALIASES_BY_PROVIDER = {
    [CODEX_DRIVER_KIND]: {
        "gpt-5-codex": "gpt-5.4",
        "5.4": "gpt-5.4",
        "5.3": "gpt-5.3-codex",
        "gpt-5.3": "gpt-5.3-codex",
        "5.3-spark": "gpt-5.3-codex-spark",
        "gpt-5.3-spark": "gpt-5.3-codex-spark",
    },
    [CLAUDE_DRIVER_KIND]: {
        opus: "claude-opus-4-8",
        "opus-4.8": "claude-opus-4-8",
        "claude-opus-4.8": "claude-opus-4-8",
        "opus-4.7": "claude-opus-4-7",
        "claude-opus-4.7": "claude-opus-4-7",
        "opus-4.6": "claude-opus-4-6",
        "claude-opus-4.6": "claude-opus-4-6",
        "claude-opus-4-6-20251117": "claude-opus-4-6",
        sonnet: "claude-sonnet-4-6",
        "sonnet-4.6": "claude-sonnet-4-6",
        "claude-sonnet-4.6": "claude-sonnet-4-6",
        "claude-sonnet-4-6-20251117": "claude-sonnet-4-6",
        haiku: "claude-haiku-4-5",
        "haiku-4.5": "claude-haiku-4-5",
        "claude-haiku-4.5": "claude-haiku-4-5",
        "claude-haiku-4-5-20251001": "claude-haiku-4-5",
    },
};
// ── Provider display names ────────────────────────────────────────────
export const PROVIDER_DISPLAY_NAMES = {
    [CODEX_DRIVER_KIND]: "Codex",
    [CLAUDE_DRIVER_KIND]: "Claude",
};
